import "./connect.less"
import { getElementPadding, hasText, parsePadding } from "utils/helpers"
import {
  maxCountConnectPairs,
  PlayConnectItem,
  PlayConnectPair,
  PlayConnectState,
  PlayHubState,
  SelectPairPayload,
} from "../play-model"
import PresentationQuestion from "../components/media/presentation-question"
import {
  forwardRef,
  Fragment,
  RefObject,
  SVGLineElementAttributes,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react"
import { usePlayResponsive } from "../play-responsive-context"
import { usePlayState } from "play/hooks/use-play-state"
import { ScreenType } from "models/tables"
import { useMessages } from "play/language/language"

type Props = {
  questionText: string
  pairs: PlayConnectPair[]
  connection: PlayConnectState["connection"]
  showCorrect: boolean
  isTeacher: boolean
  onPairSelected(payload: SelectPairPayload): void
}

export default function Connect() {
  const { screen, connect, isTeacherUrl, showCorrectAnswers, modifyState } =
    usePlayState()

  function handlePairSelected(payload: SelectPairPayload) {
    modifyState((state) => {
      if (!state.screen) {
        console.warn("No current screen!")
        return
      }
      if (state.screen.screenType !== ScreenType.Connect) {
        console.warn("Not a connect screen!")
        return
      }
      if (state.isTeacherUrl) {
        // teacher cannot change
        return
      }

      if (state.finished) return

      const connect = state.connect
      const connection = connect.connection
      const pairs = connect.pairs

      let mySide: typeof payload.type
      let otherSide: typeof payload.type

      if (payload.type === "left") {
        mySide = "left"
        otherSide = "right"
      } else if (payload.type === "right") {
        mySide = "right"
        otherSide = "left"
      } else {
        console.warn("Unnown payload type:", payload)
        return
      }

      function pushConnectColor(
        connect: PlayHubState["connect"],
        colorIndex: number
      ): boolean {
        const { remainingColors } = connect
        if (colorIndex >= 0 && colorIndex < maxCountConnectPairs) {
          if (remainingColors.indexOf(colorIndex) === -1) {
            remainingColors.splice(0, 0, colorIndex)
            return true
          } else {
            return false
          }
        } else {
          return false
        }
      }

      function popConnectColor(connect: PlayHubState["connect"]): number {
        const { remainingColors } = connect
        if (remainingColors.length > 0) {
          return remainingColors.splice(0, 1)[0]
        } else {
          return -1
        }
      }

      function deselectConnectItem(what: PlayConnectItem) {
        what.connectedToIdx = -1
        what.colorIndex = -1
        what.isSelected = false
      }

      function deselectConnectPairOnMySide(
        connect: PlayHubState["connect"],
        pair: PlayConnectPair,
        mySide: SelectPairPayload["type"],
        otherSide: SelectPairPayload["type"]
      ) {
        const myItem = pair[mySide]
        const colorIndex = myItem.colorIndex

        if (myItem.connectedToIdx !== -1) {
          const otherPair = connect.pairs[myItem.connectedToIdx]
          const otherItem = otherPair[otherSide]

          deselectConnectItem(otherItem)
        }
        deselectConnectItem(myItem)

        pushConnectColor(connect, colorIndex)
      }

      const myPairIndex = pairs.findIndex((x) => x[mySide].id === payload.id)

      if (myPairIndex === -1) {
        console.warn("Cannot find my pair %s!", payload.id)
        return
      }

      const myPair = pairs[myPairIndex]
      const myItem = myPair[mySide]

      if (myItem.isSelected && myItem.connectedToIdx !== -1) {
        deselectConnectPairOnMySide(connect, myPair, mySide, otherSide)
        if (
          connection[otherSide] === connection[mySide] &&
          connection[mySide] === -1
        ) {
          myItem.isSelected = true
        }
      }

      myItem.isSelected = !myItem.isSelected
      if (myItem.isSelected) {
        // break up connection on my side
        if (connection[mySide] !== -1) {
          deselectConnectPairOnMySide(
            connect,
            pairs[connection[mySide]],
            mySide,
            otherSide
          )
          connection[mySide] = -1
        }

        if (connection[otherSide] !== -1) {
          // form a connection
          const otherPair = pairs[connection[otherSide]]
          const otherItem = otherPair[otherSide]

          myItem.connectedToIdx = connection[otherSide]
          myItem.colorIndex = otherItem.colorIndex
          otherItem.connectedToIdx = myPairIndex

          connection[mySide] = connection[otherSide] = -1
        } else {
          // select for the first time on my side and establish connection on my side
          myItem.colorIndex = popConnectColor(connect)
          connection[mySide] = myPairIndex
        }
      } else {
        // deselect on my side and break up possible connection
        deselectConnectPairOnMySide(connect, myPair, mySide, otherSide)
        connection[mySide] = -1
      }

      const countConnected = pairs.reduce(
        (sum, pair) =>
          pair.left.connectedToIdx !== -1 && pair.right.connectedToIdx !== -1
            ? sum + 1
            : sum,
        0
      )

      state.nextScreenButtonIsActive = countConnected >= pairs.length
    })
  }

  return (
    <ConnectScreenView
      questionText={screen?.questionText || ""}
      pairs={connect.pairs}
      connection={connect.connection}
      showCorrect={showCorrectAnswers}
      isTeacher={isTeacherUrl}
      onPairSelected={handlePairSelected}
    />
  )
}

type ConnectItemProps = {
  pair: PlayConnectPair
  type: "left" | "right"
  correct: "yes" | "no" | "unset"
  isTeacher: boolean
  selectPair: (payload: SelectPairPayload) => void
}

export function ConnectScreenView(props: Props) {
  const msg = useMessages()
  const { pairs, showCorrect, onPairSelected, isTeacher, questionText } = props

  const leftRefs: RefObject<HTMLDivElement>[] = []
  const rightRefs: RefObject<HTMLDivElement>[] = []

  const MAX_PAIRS = 5
  for (let i = 0; i < MAX_PAIRS; i++) {
    leftRefs[i] = useRef<HTMLDivElement>(null)
    rightRefs[i] = useRef<HTMLDivElement>(null)
  }

  const rootRef = useRef<HTMLDivElement>(null)
  const svgRef = useRef<SVGSVGElement>(null)

  const responsive = usePlayResponsive()

  const [state, setState] = useState({
    colors: [] as string[],
    correctColor: "",
    incorrectColor: "",
    canvasWidth: 0,
    canvasHeight: 0,
  })

  useEffect(() => {
    function calculateSizes() {
      if (!rootRef.current) return

      const rootRt = rootRef.current.getBoundingClientRect()
      const rootStyle = window.getComputedStyle(rootRef.current)
      const containerWidth =
        rootRt.width -
        (parsePadding(rootStyle.paddingLeft) +
          parsePadding(rootStyle.paddingRight))
      const containerHeight =
        rootRt.height -
        (parsePadding(rootStyle.paddingTop) +
          parsePadding(rootStyle.paddingBottom))

      setState((prev) => ({
        ...prev,
        canvasWidth: containerWidth,
        canvasHeight: containerHeight,
      }))
    }

    window.addEventListener("resize", calculateSizes)

    calculateSizes()

    return () => {
      window.removeEventListener("resize", calculateSizes)
    }
  }, [pairs.length, rootRef.current])

  const connectingLines = useMemo(() => {
    // const connectingLines = function() {
    if (!svgRef.current) {
      return []
    }

    const svgRt = svgRef.current.getBoundingClientRect()

    function toLocalSpace(pt: Point): Point {
      return point(pt.x - svgRt.x, pt.y - svgRt.y)
    }

    // console.log({pairs, showCorrect})
    const result: ConnectingLine[] = []

    for (let [idx, p] of pairs.entries()) {
      if (p.left.connectedToIdx === -1) continue

      const srcElem = leftRefs[idx]
      const destElem = rightRefs[p.left.connectedToIdx]

      let className: string
      if (showCorrect) {
        const other = pairs[p.left.connectedToIdx].right
        const isCorrect = p.left.isCorrect === true && other.isCorrect === true

        if (isCorrect) {
          className = "correct"
        } else {
          className = "incorrect"
        }
      } else {
        className = "color-" + (p.left.colorIndex + 1)
      }

      const srcRt = srcElem.current!.getBoundingClientRect()
      // const srcPadding = getElementPadding(srcElem.current!)
      const destRt = destElem.current!.getBoundingClientRect()
      const destPadding = getElementPadding(destElem.current!)

      let from: Point
      let to: Point
      if (responsive.isPortraitMode) {
        from = {
          x: srcRt.right,
          y: srcRt.top + srcRt.height / 2,
        }
        to = {
          x: destRt.left,
          y: destRt.top + destRt.height / 2,
        }
      } else {
        from = {
          x: srcRt.left + srcRt.width / 2,
          y: srcRt.bottom,
        }
        to = {
          x: destRt.left + destRt.width / 2,
          y: destRt.top - destPadding.top,
        }
      }

      from = toLocalSpace(from)
      to = toLocalSpace(to)

      result.push({
        from,
        to,
        className,
      })
    }

    return result
  }, [pairs, state.canvasWidth, state.canvasHeight, responsive.isPortraitMode])

  // }();

  function correct(item: PlayConnectItem): ConnectItemProps["correct"] {
    if (showCorrect && item.isCorrect !== null) {
      return item.isCorrect ? "yes" : "no"
    } else {
      return "unset"
    }
  }

  const sizeClass = `size-${pairs.length}`

  return (
    <div className={`connect-screen ${sizeClass} play-screen`}>
      <div className="connect-top">
        {hasText(questionText) && (
          <PresentationQuestion
            question={questionText}
            reduceSizeOnSmallerScreens={true}
          />
        )}
      </div>
      <div
        className={`connect-content ${sizeClass}`}
        ref={rootRef}
        aria-label={msg.connectPairsLabel}
      >
        <div className="background">
          {pairs.map((pair, pairIndex) => (
            <Fragment key={pair.left.id + pair.right.id}>
              <ConnectItem
                correct={correct(pair.left)}
                pair={pair}
                type="left"
                selectPair={onPairSelected}
                isTeacher={isTeacher}
                ref={leftRefs[pairIndex]}
              />
              <ConnectItem
                correct={correct(pair.right)}
                pair={pair}
                type="right"
                selectPair={onPairSelected}
                isTeacher={isTeacher}
                ref={rightRefs[pairIndex]}
              />
            </Fragment>
          ))}
        </div>
        <svg
          className="arrows"
          width={state.canvasWidth}
          height={state.canvasHeight}
          // viewBox={`0 0 ${state.canvasWidth} ${state.canvasHeight}`}
          ref={svgRef}
        >
          {connectingLines.map((line) => (
            <>
              <LineWithArrow
                key={`${line.from.x},${line.from.y}_${line.to.x},${line.to.y}_outline`}
                from={line.from}
                to={line.to}
                className={"black"}
                isPortraitMode={responsive.isPortraitMode}
              />
              <LineWithArrow
                key={`${line.from.x},${line.from.y}_${line.to.x},${line.to.y}`}
                from={line.from}
                to={line.to}
                className={line.className}
                isPortraitMode={responsive.isPortraitMode}
              />
            </>
          ))}
        </svg>
      </div>
    </div>
  )
}

function LineWithArrow(
  props: ConnectingLine & {
    isPortraitMode: boolean
  }
) {
  const { from, to, className, isPortraitMode, width } = props

  const gap = 5
  const arrowLength = 15
  const arrowArmLength = arrowLength * 0.4
  const distanceFromEnd = gap
  const armAngle = Math.PI / 4

  let endingLine = emptyLine(className + " ending", width)
  let arrowLine = emptyLine(className + " arrow", width)
  let arrowArm1 = emptyLine(className + " arrow_arm_1", width)
  let arrowArm2 = emptyLine(className + " arrow_arm_2", width)
  let startingLine = emptyLine(className + " starting", width)

  if (from.x !== to.x) {
    // if we have slope

    const dx = to.x - from.x
    const dy = to.y - from.y

    const lineLength = Math.sqrt(dx * dx + dy * dy)

    // slope
    const m = dy / dx

    // line function
    function f(x: number) {
      return m * (x - from.x) + from.y
    }

    // express advances in x as proportaions relative to line length
    function px(a: number) {
      return (a / lineLength) * dx
    }

    endingLine.x1 = from.x + px(lineLength - distanceFromEnd)
    endingLine.y1 = f(endingLine.x1)
    endingLine.x2 = to.x
    endingLine.y2 = to.y

    arrowLine.x1 = from.x + px(lineLength - distanceFromEnd - gap)
    arrowLine.y1 = f(arrowLine.x1)
    arrowLine.x2 = from.x + px(lineLength - distanceFromEnd - gap - arrowLength)
    arrowLine.y2 = f(arrowLine.x2)

    startingLine.x1 =
      from.x + px(lineLength - distanceFromEnd - gap - arrowLength - gap)
    startingLine.y1 = f(startingLine.x1)
    startingLine.x2 = from.x
    startingLine.y2 = from.y

    const armRotationStartX =
      from.x + px(lineLength - distanceFromEnd - gap - arrowArmLength)
    const armRotationStartY = f(armRotationStartX)
    const armRotationOrigin = point(arrowLine.x1, arrowLine.y1)

    const arrowArmStart1 = rotatePointByAngleFromOrigin({
      pt: point(armRotationStartX, armRotationStartY),
      origin: armRotationOrigin,
      angleInRads: -armAngle,
    })
    arrowArm1.x1 = arrowArmStart1.x
    arrowArm1.y1 = arrowArmStart1.y
    arrowArm1.x2 = arrowLine.x1
    arrowArm1.y2 = arrowLine.y1

    const arrowArmStart2 = rotatePointByAngleFromOrigin({
      pt: point(armRotationStartX, armRotationStartY),
      origin: armRotationOrigin,
      angleInRads: armAngle,
    })

    arrowArm2.x1 = arrowArmStart2.x
    arrowArm2.y1 = arrowArmStart2.y
    arrowArm2.x2 = arrowLine.x1
    arrowArm2.y2 = arrowLine.y1
  } else {
    // straight vertical line

    startingLine.x1 =
      startingLine.x2 =
      arrowLine.x1 =
      arrowLine.x2 =
      arrowArm1.x1 =
      arrowArm1.x2 =
      arrowArm2.x1 =
      arrowArm2.x2 =
      endingLine.x1 =
      endingLine.x2 =
        from.x

    startingLine.y1 = from.y
    startingLine.y2 = to.y - gap * 2 - distanceFromEnd - arrowLength

    arrowLine.y1 = startingLine.y2 + gap
    arrowLine.y2 = arrowLine.y1 + arrowLength

    endingLine.y1 = arrowLine.y2 + gap
    endingLine.y2 = to.y

    const armRotationStart = point(from.x, arrowLine.y1 + arrowArmLength)
    const armRotationOrigin = point(arrowLine.x2, arrowLine.y2)

    const arrowArmStart1 = rotatePointByAngleFromOrigin({
      pt: armRotationStart,
      origin: armRotationOrigin,
      angleInRads: -armAngle,
    })
    arrowArm1.x1 = arrowArmStart1.x
    arrowArm1.y1 = arrowArmStart1.y
    arrowArm1.y2 = arrowLine.y2

    const arrowArmStart2 = rotatePointByAngleFromOrigin({
      pt: armRotationStart,
      origin: armRotationOrigin,
      angleInRads: armAngle,
    })
    arrowArm2.x1 = arrowArmStart2.x
    arrowArm2.y1 = arrowArmStart2.y
    arrowArm2.y2 = arrowLine.y2
  }

  return (
    <Fragment>
      <line {...startingLine} />
      <line {...arrowLine} />
      <line {...arrowArm1} />
      <line {...arrowArm2} />
      <line {...endingLine} />
    </Fragment>
  )
}

const ConnectItem = forwardRef(function (props: ConnectItemProps, ref) {
  const msg = useMessages()
  const { pair, type, selectPair, correct, isTeacher } = props
  const item = pair[type]

  let additionalClasses = ""
  if (item.isSelected) {
    additionalClasses += " selected color" + (item.colorIndex + 1)
  }

  if (isTeacher) {
    additionalClasses += " teacher"
  }

  if (correct === "yes") {
    additionalClasses += " correct"
  } else if (correct === "no") {
    additionalClasses += " incorrect"
  }

  let icon: JSX.Element | null = null
  if (type === "right" && correct !== "unset") {
    if (correct === "yes") {
      icon = <i className="correct" />
    } else {
      icon = <i className="incorrect" />
    }
  }

  if (hasText(item.url)) {
    additionalClasses += " " + item.type

    return (
      <div className={`item ${type}`}>
        {/*<div className="img-wrapper">*/}
        <img
          ref={ref as RefObject<HTMLImageElement>}
          draggable={false}
          className={additionalClasses}
          src={item.url}
          alt={
            type === "left"
              ? msg.connectPairLeftImageAlt
              : msg.connectPairRightImageAlt
          }
          onClick={() => selectPair({ type, id: item.id })}
        />
        {icon}
        {/*</div>*/}
      </div>
    )
  } else {
    return (
      <div className={`item with-text ${type}`}>
        <button
          draggable={false}
          ref={ref as RefObject<HTMLButtonElement>}
          className={`text ${additionalClasses}`}
          onClick={() => selectPair({ type, id: item.id })}
          aria-label={
            type === "left" ? msg.connectLeftPairText : msg.connectPairRightText
          }
        >
          {item.text}
        </button>
        {icon}
      </div>
    )
  }
})

interface Point {
  x: number
  y: number
}

type LineData = SVGLineElementAttributes<SVGLineElement>

function emptyLine(className: string, width?: number): LineData {
  const line: LineData = {
    x1: 0,
    y1: 0,
    x2: 0,
    y2: 0,
    className,
  }
  if (width) {
    line.strokeWidth = width
  }
  return line
}

interface ConnectingLine {
  from: Point
  to: Point
  className: string
  width?: number
}

function point(x: number, y: number): Point {
  return {
    x,
    y,
  }
}

function rotatePointByAngleFromOrigin(params: {
  pt: Point
  origin: Point
  angleInRads: number
}): Point {
  const { pt, origin, angleInRads } = params
  // https://en.wikipedia.org/wiki/Rotation_matrix
  // https://www.euclideanspace.com/maths/geometry/affine/aroundPoint/matrix2d/
  const sin = Math.sin(angleInRads)
  const cos = Math.cos(angleInRads)

  const r00 = cos,
    r01 = -sin,
    r10 = sin,
    r11 = cos
  const xin = pt.x
  const yin = pt.y
  const x = origin.x
  const y = origin.y

  return {
    x: r00 * xin + r01 * yin + x - r00 * x - r01 * y,
    y: r10 * xin + r11 * yin + y - r10 * x - r11 * y,
  }
}
